/**
 * Application-level attention contract (见 23 / §5.15).
 *
 * Two things live here, and keeping them apart is the whole design:
 *
 * - **{@link AppAttention} — persistent state.** "There are n unhandled things
 *   here." Drawn as a dock/taskbar badge, a Windows overlay icon, a favicon
 *   badge, a `(3)` title prefix. **Only the owning module clears it** — the
 *   platform must never wipe it just because a window got focused, or an unread
 *   count would vanish while the messages are still unread.
 * - **{@link AttentionRequest} — a transient pulse.** "Pull the user back here
 *   now." Drawn as `flashFrame` on Windows, a dock bounce on macOS, alternating
 *   title/favicon on the web. **The platform** ends it as soon as the app is
 *   seen again, **or** as soon as the persistent state becomes empty — a pulse
 *   says "something is waiting", and it no longer is.
 *
 * The visibility gate belongs to the platform, not the caller: a module cannot
 * see cross-window visibility, and each runtime already answers "is the user
 * looking at the app" in exactly one place (desktop main asks the workbench
 * window `isFocused()`; the web host reads every connection's reported
 * `hibro:visibility`). A module says how urgent its event is — see
 * {@link AttentionUrgency} — and the platform decides whether that clears the
 * gate.
 */
import type { Disposer } from './event';

/**
 * EventBus event fired whenever the aggregated {@link AppAttention} changes.
 *
 * Deliberately **not** in `PLATFORM_PUBLIC_EVENTS`: the payload carries
 * `byModule`, which would tell a sandboxed surface what else is installed (same
 * reasoning as `LAUNCHER_CHANGED_EVENT`).
 */
export const ATTENTION_CHANGED_EVENT = 'platform.attention.changed';

/**
 * EventBus event fired when「用户此刻在不在看应用」flips (见 23 §2).
 *
 * The fact itself is reported into the host by each shell (`setAppVisible`) and
 * is what the notification router and the attention pulse gate both read. This
 * broadcast is the **module-facing** face of it, paired with
 * `ModuleContext.isAppVisible()` exactly as `LOCALE_CHANGED_EVENT` is paired
 * with `getLocale()`: pull for the current value, subscribe for the flips.
 *
 * A module must not use it to re-implement the visibility gate — that is what
 * {@link AttentionUrgency} is for. What it *is* for is the work that only makes
 * sense at the moment the user comes back, which nothing else can express:
 * IM withdraws the notification for the conversation still open on screen
 * (需求 §4.4.3「或在应用内已读」), since the user has now read it by looking.
 *
 * A platform public event (like the theme and locale changes): it carries one
 * boolean about the app as a whole and nothing about any module.
 */
export const APP_VISIBILITY_CHANGED_EVENT = 'platform.visibility.changed';

/** Payload of {@link APP_VISIBILITY_CHANGED_EVENT}. */
export interface AppVisibilityChange {
  /** Whether the user is now looking at the app. */
  readonly visible: boolean;
}

/**
 * One module's persistent attention state, as its provider reports it.
 *
 * `count` and `dot` are the same claim at two precisions — "n unhandled things"
 * versus "something is unhandled but not countable". A provider reporting both
 * has its `dot` absorbed by the count during aggregation.
 */
export interface ModuleAttention {
  /** Unhandled item count; `0` or absent means nothing pending. */
  readonly count?: number;
  /** Something is pending but has no meaningful count. */
  readonly dot?: boolean;
  /**
   * Some of what is pending deserves a stronger badge (需求 §4.5.1).
   *
   * The platform never learns *what* makes it stronger — that judgement is the
   * module's, exactly like「哪些算进这个数」(见 23 §4). IM raises it for an unread
   * @mention or a direct chat; the platform only paints those two the same badge
   * in two colours.
   *
   * **Not the same axis as {@link AttentionUrgency}**, which answers "may this
   * event pass the visibility gate". A mentioned message wants a red bubble *and*
   * a `normal` pulse: it must not interrupt someone already looking at the app.
   * One field would force those two answers to agree.
   *
   * **No sandbox path, and that is deliberate.** `capabilities.setBadge(count)`
   * takes a number only, so an iframe/external module can report unread but
   * cannot recolour the badge. `urgent` ORs across modules, i.e. any reporter
   * turns the *whole app's* badge red — the same escalation shape as the pulse
   * that sandboxes are already denied (见 23 §9 / 06). Widening `setBadge` to
   * carry it needs a permission decision first, not just a wider signature.
   */
  readonly urgent?: boolean;
}

/**
 * A module-owned source of persistent attention state (见 23).
 *
 * Shape is identical to `LaunchBadgeProvider` on purpose: the platform owns
 * rendering and cross-window invalidation, the module stays the source of
 * business data. Pull + `onDidChange` rather than a push setter so the disposer
 * carries the whole lifetime — pushing would leave a stale badge behind when the
 * module deactivates.
 */
export interface AttentionProvider {
  read(): ModuleAttention | undefined;
  readonly onDidChange?: (listener: () => void) => Disposer;
  /**
   * Native chrome asked to clear this module's pending work (见 23 托盘
   * 「标记为已读」). Optional: a provider that cannot retract its own claim
   * omits it, and the tray row stays hidden for that module.
   *
   * Sandboxed `setBadge` has no equivalent — same boundary as the pulse
   * (见 23 §9). The platform never invents a clear for a number it did not
   * get a callback for.
   */
  clear?(): void | Promise<void>;
}

/** One module's contribution to the aggregate, after normalization. */
export interface ModuleAttentionEntry {
  readonly moduleId: string;
  readonly count: number;
  readonly dot: boolean;
  readonly urgent: boolean;
  /**
   * This module's in-process provider implemented {@link AttentionProvider.clear}
   * and is still contributing. Sandbox `setBadge` reports are never clearable.
   */
  readonly clearable: boolean;
}

/**
 * The aggregated, app-level attention state (见 23) — a single projection every
 * window and every runtime driver renders from.
 */
export interface AppAttention {
  /** Sum of every provider's count. */
  readonly count: number;
  /** True when some provider reports a dot and nothing reports a count. */
  readonly dot: boolean;
  /**
   * Any contributing module wants the stronger badge (见
   * {@link ModuleAttention.urgent}). One badge carries one colour, so this is an
   * OR: with an @mention among a hundred ordinary unreads, the mention is the
   * part the user has to know about.
   */
  readonly urgent: boolean;
  /**
   * At least one still-active in-process provider can retract its claim
   * ({@link AttentionProvider.clear}). Native chrome (the tray's「标记为已读」)
   * reads this to decide whether the row would do anything.
   */
  readonly clearable: boolean;
  /** Per-module breakdown, for attribution and the settings/status surfaces. */
  readonly byModule: readonly ModuleAttentionEntry[];
}

/** The empty aggregate — nothing pending anywhere. */
export const EMPTY_APP_ATTENTION: AppAttention = {
  count: 0,
  dot: false,
  urgent: false,
  clearable: false,
  byModule: [],
};

/**
 * How badly an event wants through the platform's visibility gate (见 23).
 *
 * A generic axis rather than a `force` flag, and deliberately the same words the
 * underlying platforms already use (`app.dock.bounce('informational' |
 * 'critical')`, freedesktop notification urgency's `low | normal | critical`):
 *
 * - `low` — **only worth raising while the user is away.** For an event the app
 *   itself already puts on screen: a message in the conversation the user has
 *   open, a download finishing in a visible download panel. Nothing at all
 *   happens while the app is visible.
 * - `normal` — gated. A pulse is dropped while the user is already looking; a
 *   notification degrades to an in-app toast.
 * - `critical` — ungated. For something that must interrupt regardless, like an
 *   incoming call.
 *
 * **`low` and `normal` coincide for a pulse** and differ only for a notification
 * (see `decideNotificationRoute`): a pulse has no quieter form to degrade to —
 * pulling the user back *is* its whole content — whereas a notification has two
 * surfaces, and the rung `low` adds is the one below the in-app toast.
 *
 * Why a module gets to say this at all, when it cannot see visibility: the claim
 * is about **its own event** ("this is redundant when you are looking at us"),
 * not about the user's state. The platform still owns the judgement — which is
 * exactly why a module must not do what IM used to and drop such a notification
 * itself: it was then suppressed while the user sat in the tray, too.
 */
export type AttentionUrgency = 'low' | 'normal' | 'critical';

/** A module's request to pull the user's attention back to the app (见 23). */
export interface AttentionRequest {
  /** Defaults to `normal` (gated on the app not being visible). */
  readonly urgency?: AttentionUrgency;
  /**
   * Free-form attribution for diagnostics, e.g. `im.newMessage`. The platform
   * never interprets it; it only appears in logs.
   */
  readonly reason?: string;
}

/** An {@link AttentionRequest} after the platform filled in its defaults. */
export interface AttentionPulse {
  readonly moduleId: string;
  readonly urgency: AttentionUrgency;
  readonly reason?: string;
}

/**
 * The in-process module face for application attention (见 23), mirroring
 * `context.launcher` / `overlay` / `window`: only `source` modules hold a
 * `ModuleContext`, so a downloaded sandboxed module cannot grab the user's
 * attention through here. Its unread count still reaches the same aggregate —
 * through the permission-gated `capabilities.setBadge` — but a pulse does not.
 *
 * Two shapes for one concept because they are two concepts: state is registered
 * (and revoked with the module), a pulse is fired.
 */
export interface AttentionApi {
  /**
   * Supply this module's persistent unread/pending state. Returns a disposer —
   * push it onto `subscriptions` so the badge goes away with the module.
   */
  registerProvider(provider: AttentionProvider): Disposer;
  /**
   * Ask the platform to pull the user back to the app. Fire-and-forget: whether
   * anything visibly happens depends on `urgency`, the current visibility, and
   * the user's `workbench.attention.flash` setting.
   */
  request(request?: AttentionRequest): void;
}

/** What a badge decoration shows: nothing, a bare marker, or a number. */
export type BadgeShape =
  | { readonly kind: 'none' }
  | { readonly kind: 'dot'; readonly urgent: boolean }
  | {
      readonly kind: 'count';
      readonly label: string;
      readonly urgent: boolean;
    };

/**
 * Ordinary unread disc (需求 §4.5.1「普通未读」). Same red the Windows overlay
 * and the web favicon already used before the two-tone split.
 *
 * Shared so every surface that *can* show colour paints the same red — the
 * Windows overlay, the Windows/Linux tray composite, the web favicon. Surfaces
 * that cannot (window title, macOS Dock count, macOS tray title) ignore it.
 */
export const BADGE_DISC_COLOR = { r: 220, g: 54, b: 60 } as const;

/**
 * Stronger unread disc (需求 §4.5.1「@mention / DM」).
 *
 * **Placeholder — pending product confirmation.** Distinct from
 * {@link BADGE_DISC_COLOR} so the two states are already distinguishable; swap
 * this one constant when the colour is signed off.
 */
export const BADGE_URGENT_DISC_COLOR = { r: 196, g: 0, b: 0 } as const;

export type BadgeDiscColor =
  | typeof BADGE_DISC_COLOR
  | typeof BADGE_URGENT_DISC_COLOR;

/** Pick the disc colour a {@link BadgeShape} should paint. */
export function badgeDiscColor(shape: BadgeShape): BadgeDiscColor {
  return shape.kind !== 'none' && shape.urgent
    ? BADGE_URGENT_DISC_COLOR
    : BADGE_DISC_COLOR;
}

/** `#rrggbb` form of a disc colour, for canvas / CSS painters. */
export function badgeDiscCss(color: BadgeDiscColor): string {
  const hex = (channel: number): string =>
    channel.toString(16).padStart(2, '0');
  return `#${hex(color.r)}${hex(color.g)}${hex(color.b)}`;
}

/**
 * Reduce an aggregate to what a badge can actually show (见 23). Shared by every
 * runtime's driver — a Windows taskbar overlay, a favicon, a title prefix — so
 * the same unread count reads the same everywhere.
 *
 * The label saturates at `99+` because the smallest of those surfaces is roughly
 * 16 device pixels wide: a fourth character stops being a number and becomes a
 * smudge, and "more than 99" is all the precision anyone reads off an app icon.
 *
 * `urgent` rides along on the `dot` / `count` shapes so the four painters that
 * *can* show colour (Windows overlay, Windows/Linux tray, web favicon) pick the
 * same disc without each re-reading the aggregate. A `none` has nowhere to put
 * a colour — that is the corner where an @mention in a muted chat raises
 * `urgent` but `count` is still 0 (见 IM `hasUrgentUnread`).
 */
export function describeBadge(state: AppAttention): BadgeShape {
  if (state.count > 0) {
    return {
      kind: 'count',
      label: state.count > 99 ? '99+' : String(state.count),
      urgent: state.urgent,
    };
  }
  return state.dot ? { kind: 'dot', urgent: state.urgent } : { kind: 'none' };
}

/**
 * Normalize one provider reading. A negative or non-finite count is treated as
 * absent rather than propagated — a badge is a display value, and `NaN` would
 * poison the whole sum.
 */
export function normalizeModuleAttention(
  moduleId: string,
  state: ModuleAttention | undefined,
  clearable = false,
): ModuleAttentionEntry {
  const raw = state?.count;
  const count =
    typeof raw === 'number' && Number.isFinite(raw) && raw > 0
      ? Math.floor(raw)
      : 0;
  return {
    moduleId,
    count,
    dot: state?.dot === true,
    urgent: state?.urgent === true,
    clearable,
  };
}

/**
 * Fold per-module entries into the app-level aggregate. Entries contributing
 * nothing are dropped so `byModule` reads as "who currently wants attention".
 */
export function aggregateAttention(
  entries: readonly ModuleAttentionEntry[],
): AppAttention {
  const active = entries.filter((entry) => entry.count > 0 || entry.dot);
  const count = active.reduce((sum, entry) => sum + entry.count, 0);
  // A count is strictly more informative than a dot, so it absorbs it: showing
  // both "3" and a dot would be two decorations for one fact.
  //
  // `urgent` is an OR across whoever is still contributing: one @mention among
  // a hundred ordinary unreads is the part the user has to know about. An
  // inactive module's leftover `urgent: true` must not leak in, which is why
  // the fold only looks at `active`.
  return {
    count,
    dot: count === 0 && active.length > 0,
    urgent: active.some((entry) => entry.urgent),
    clearable: active.some((entry) => entry.clearable),
    byModule: active,
  };
}
